import { inject, Injectable } from '@angular/core';
import { TagService } from './tag.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { Tag } from './Tag';
import {
  addDoc,
  collection,
  collectionData,
  CollectionReference,
  deleteDoc,
  doc,
  Firestore,
  query,
  updateDoc,
  writeBatch,
} from '@angular/fire/firestore';
import { AuthService } from 'src/services/auth/auth.service';
import { isParent } from 'src/utils/isParent';
import { archiveSelected } from 'src/utils/archiveSelected';

@Injectable({
  providedIn: 'root',
})
export class FirestoreTagService implements TagService {
  firestore: Firestore = inject(Firestore);
  authService: AuthService = inject(AuthService);
  public tags = new BehaviorSubject<Tag[]>([]);

  private localTags: Tag[] = [];
  private readonly MAX_LEVEL = 4;
  public static readonly DEFAULT_COLOR = 'green';

  private getCollectionPath() {
    return `/users/${this.authService.user?.uid}/tags`;
  }

  constructor() {
    const collectionReference = collection(
      this.firestore,
      this.getCollectionPath()
    );

    const tagsObservable = collectionData(query(collectionReference), {
      idField: 'id',
    }) as Observable<Tag[]>;

    tagsObservable.subscribe((tags) => {
      this.localTags = tags;
      this.tags.next(tags);
    });
  }

  updateById(newTag: Tag): Promise<void> {
    const index = this.localTags.findIndex((t) => t.id === newTag.id);
    const oldTag = this.localTags[index];
    if (oldTag.fullPath === newTag.fullPath) {
      return updateDoc(
        doc(this.firestore, `${this.getCollectionPath()}/${newTag.id}`),
        { ...newTag }
      );
    } else {
      const pathParts = newTag.fullPath.split('/');

      if (pathParts.length > this.MAX_LEVEL) {
        throw new Error('Tag max deepness: ' + this.MAX_LEVEL);
      } else {
        const children = this.localTags.filter((t) =>
          isParent(oldTag.fullPath, t.fullPath)
        );

        children.forEach((child) => {
          const childPathParts = child.fullPath.split('/');
          const newPathParts = pathParts.concat(
            childPathParts.slice(oldTag.fullPath.split('/').length)
          );
          child.fullPath = newPathParts.join('/');

          updateDoc(
            doc(this.firestore, `${this.getCollectionPath()}/${child.id}`),
            {
              ...child,
            }
          );
        });

        return updateDoc(
          doc(this.firestore, `${this.getCollectionPath()}/${newTag.id}`),
          { ...newTag }
        );
      }
    }
  }

  deleteById(id: string): Promise<any> {
    const tagsToDelete = [id];
    const tag = this.localTags.find((t) => t.id === id);
    if (tag) {
      const pathParts = tag.fullPath.split('/');
      this.localTags.forEach((t) => {
        const tPathParts = t.fullPath.split('/');

        if (
          t.id &&
          tPathParts.length > pathParts.length &&
          tPathParts.slice(0, pathParts.length).join('/') === tag.fullPath
        ) {
          tagsToDelete.push(t.id);
        }
      });
    }

    const deleteTags: Promise<void>[] = tagsToDelete.map((id) =>
      deleteDoc(doc(this.firestore, `${this.getCollectionPath()}/${id}`))
    );
    return Promise.all(deleteTags);
  }

  /**
   * Creates new tags, updates existing ones and deletes tags that are not in the new list.
   */
  save(tags: Tag[]): Promise<void> {
    const batch = writeBatch(this.firestore);

    tags.forEach((tag) => {
      const id =
        tag.id ?? doc(collection(this.firestore, this.getCollectionPath())).id;
      const tagWithoutId = Object.fromEntries(
        Object.entries(tag).filter(([k]) => k !== 'id')
      );

      const cleanedEntry = Object.fromEntries(
        Object.entries(tagWithoutId).filter(
          ([key, value]) => value !== undefined && key !== 'id'
        )
      );

      batch.set(doc(this.firestore, `${this.getCollectionPath()}/${id}`), {
        ...cleanedEntry,
      });
    });

    this.localTags.forEach((localTag) => {
      if (!tags.find((t) => t.id === localTag.id)) {
        batch.delete(
          doc(this.firestore, `${this.getCollectionPath()}/${localTag.id}`)
        );
      }
    });

    return batch.commit();
  }

  async create(fullPath: string, isPeriodic?: boolean): Promise<string> {
    if (fullPath.split('/').length > this.MAX_LEVEL) {
      throw new Error('Tag max deepness: ' + this.MAX_LEVEL);
    }
    await this.createSubPathsIfNecessary(fullPath);

    const level = fullPath.split('/').length;
    const location = fullPath.split('/').slice(0, -1).join('/');

    const tagsOnSameLevel = this.localTags.filter(
      (tag) =>
        tag.fullPath.split('/').length === level &&
        tag.fullPath.startsWith(location)
    );

    const newTag: Tag = {
      fullPath,
      color: FirestoreTagService.DEFAULT_COLOR,
      isArchived: false,
      isPeriodic,
    };

    const cleanedEntry = Object.fromEntries(
      Object.entries(newTag).filter(
        ([key, value]) => value !== undefined && key !== 'id'
      )
    );

    const doc = await addDoc(
      collection(this.firestore, this.getCollectionPath()),
      cleanedEntry
    );

    return doc.id;
  }
  async createSubPathsIfNecessary(fullPath: string): Promise<void> {
    const parts = fullPath.split('/');
    for (let i = 1; i < parts.length; i++) {
      const subPath = parts.slice(0, i).join('/');
      if (!this.localTags.find((tag) => tag.fullPath === subPath)) {
        await this.create(subPath);
      }
    }
  }

  public getNewId(): string {
    return doc(collection(this.firestore, this.getCollectionPath())).id;
  }

  public duplicate(id: string): Promise<void> {
    throw new Error('Method not implemented.');
  }

  public archiveWithTree(tag: Tag, setToArchiveTrue = true): Promise<void> {
    const updatedTags = archiveSelected(tag, this.localTags, setToArchiveTrue);
    if (updatedTags) {
      return this.save(updatedTags);
    } else {
      return Promise.reject();
    }
  }

  public archive(id: string): Promise<void> {
    return updateDoc(doc(this.firestore, `${this.getCollectionPath()}/${id}`), {
      isArchived: true,
    });
  }

  public unarchive(id: string): Promise<void> {
    return updateDoc(doc(this.firestore, `${this.getCollectionPath()}/${id}`), {
      isArchived: false,
    });
  }
}
